/**
 * Owner: Masa Bayu
 * File: MarketplaceCategoryStrip.tsx
 * Date:Time: 2026-10-19 13:05
 * Description: §392/§406 — strip kategori halaman All Books (<768): dua baris
 *   sahaja (All + 8 kategori + tile More ▾); baki kategori dalam dropdown
 *   berlatar yang memilih penuras dan menutup panel.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import {
  MARKETPLACE_CATEGORY_ORDER,
  type MarketplaceCategoryId,
  type MarketplaceFilters,
} from './marketplace-projection';
import type { MarketplaceLabels } from './marketplace-locale';
import { LandingIcon } from './landing/LandingIcons';
import { CATEGORY_ICON } from './landing/landing-shared';
import { qxCategoryName } from '@qx/qxread-core';

/** §406 — 2 baris × 5 lajur = 10 sel: All + STRIP_CATEGORY_COUNT + More. */
const STRIP_CATEGORY_COUNT = 8;

export interface MarketplaceCategoryStripProps {
  readonly t: MarketplaceLabels;
  readonly filters: MarketplaceFilters;
  readonly onChange: (filters: MarketplaceFilters) => void;
}

export function MarketplaceCategoryStrip({
  t,
  filters,
  onChange,
}: MarketplaceCategoryStripProps): ReactElement {
  const [moreOpen, setMoreOpen] = useState(false);
  const pick = (category: MarketplaceCategoryId | 'all'): void => {
    onChange({ ...filters, category, subcategory: 'all' });
  };
  const visible = MARKETPLACE_CATEGORY_ORDER.slice(0, STRIP_CATEGORY_COUNT);
  const hidden = MARKETPLACE_CATEGORY_ORDER.slice(STRIP_CATEGORY_COUNT);
  const moreActive = hidden.includes(filters.category as MarketplaceCategoryId);
  return (
    <nav className="hmk__catstrip" aria-label={t.sidebar.categoryLegend}>
      <button
        type="button"
        className="hmk__catstrip-item"
        data-active={filters.category === 'all'}
        onClick={() => {
          pick('all');
        }}
      >
        <span className="hmk__catstrip-icon" aria-hidden="true">
          <LandingIcon name="grid" size={16} />
        </span>
        <span>{t.sidebar.allCategories}</span>
      </button>
      {visible.map((id) => (
        <button
          key={id}
          type="button"
          className="hmk__catstrip-item"
          data-active={filters.category === id}
          onClick={() => {
            pick(id);
          }}
        >
          <span className="hmk__catstrip-icon" aria-hidden="true">
            <LandingIcon name={CATEGORY_ICON[id]} size={16} />
          </span>
          <span>{qxCategoryName(id, t.language)}</span>
        </button>
      ))}
      <div className="hmk__catstrip-more">
        <button
          type="button"
          className="hmk__catstrip-item"
          data-active={moreActive}
          aria-expanded={moreOpen}
          aria-haspopup="true"
          onClick={() => {
            setMoreOpen(!moreOpen);
          }}
        >
          <span className="hmk__catstrip-icon" aria-hidden="true">
            <LandingIcon name="more" size={16} />
          </span>
          <span>{t.sidebar.moreCategories}</span>
        </button>
        {moreOpen && (
          <>
            <span
              className="hmk__catstrip-backdrop"
              aria-hidden="true"
              onClick={() => {
                setMoreOpen(false);
              }}
            />
            <ul
              className="hmk__catstrip-panel"
              role="listbox"
              aria-label={t.sidebar.moreCategories}
            >
              {hidden.map((id) => (
                <li key={id} role="option" aria-selected={filters.category === id}>
                  <button
                    type="button"
                    className="hmk__catstrip-panel-item"
                    data-active={filters.category === id}
                    onClick={() => {
                      pick(id);
                      setMoreOpen(false);
                    }}
                  >
                    <span className="hmk__catstrip-icon" aria-hidden="true">
                      <LandingIcon name={CATEGORY_ICON[id]} size={16} />
                    </span>
                    <span>{qxCategoryName(id, t.language)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </nav>
  );
}
